import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Wrapper, Name, Icon, Help, Preview } from './styled';
import SvgIcon from '../svg-icon/svg-icon';
import { StandardDropdown, MessageBubble, PreviewDemoIcon } from '../../helpers/icon-dictionary';
import { getStudents } from './util';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import ContextModal, { ContextModalWidth } from '../context-modal';
import { getContextCoords } from '../../helpers/utils';
import NeedHelp from '../need-help';
import StandardInfo from '../standard-info';
import { activateModal } from '../../containers/modal-manager/modal-manager-actions';
import { createPreviewModal } from '../preview-modal/preview-modal';

const LearningJourney = ({ ljCode, ljName, standardName, externalKey, description, id, count, i18n }) => {
  const {
    assignmentStatuses,
    classrooms
  } = useSelector(state => state.UPFAssignReducer);
  const helpSeekingStudents = getStudents(id, assignmentStatuses, classrooms);
  const [showContextMenu, setContextMenu] = useState(false);
  const [callOut, setCallOut] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [positionArrow, setPositionArrow] = useState(0);
  const [positionXCallout, setPositionXCallout] = useState(0);
  const [positionYCallout, setPositionYCallout] = useState(0);
  const [positionArrowCallout, setPositionArrowCallout] = useState(0);
  const [contextTheme, setContextTheme] = useState(null);
  const ljRef = useRef(null);
  const titleRef = useRef(null);
  const dispatch = useDispatch();

  function handleEllipsis() { // Multi-Line ellipsis
    if (titleRef.current.innerHTML) {
      const wordArray = titleRef.current.innerHTML.split(' ');
      while (titleRef.current.scrollHeight > titleRef.current.offsetHeight) {
        wordArray.pop();
        titleRef.current.innerHTML = `${wordArray.join(' ')}...`;
      }
    }
  }

  useEffect(() => {
    handleEllipsis();
  }, []);

  const getPreviewInfo = () => {
    const previewInfo = {
      domain: standardName,
      question: ljName,
      girlId: id
    };
    dispatch(activateModal(createPreviewModal({ previewInfo })));
  };

  const showContextModal = () => {
    const { posX, posY, posArrow } = getContextCoords(ljRef.current.getBoundingClientRect(), ContextModalWidth);
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setContextMenu(!showContextMenu);
    setContextTheme(null);
    setCallOut(false);
  };

  const showCallOut = () => {
    const { posX, posY, posArrow } =
      getContextCoords(ljRef.current.getBoundingClientRect(), ContextModalWidth);
    setPositionYCallout(posY);
    setPositionXCallout(posX);
    setPositionArrowCallout(posArrow);
    setContextTheme('learningJourney');
    setCallOut(true);
  };

  const hideCallOut = () => {
    setCallOut(false);
  };

  return (<Wrapper onClick={showContextModal} ref={ljRef}>
    {helpSeekingStudents.length > 0 ? (
      <Help>
        <div className="iconWrapper">
          <SvgIcon icon={MessageBubble} />
        </div>
        <div className="number">{helpSeekingStudents.length}</div>
      </Help>)
      : (
        <Icon>
          <SvgIcon icon={StandardDropdown} />
        </Icon>)}
    <Name ref={titleRef} onMouseEnter={showCallOut} onMouseLeave={hideCallOut}><span>{ljName}</span></Name>
    <OutsideClickHandler callback={() => setContextMenu(false)} forButton={`showContextModal_${id}`}>
      <ContextModal
        show={showContextMenu}
        positionArrow={positionArrow}
        positionX={positionX}
        positionY={positionY}>
        {helpSeekingStudents.length > 0 ?
          (<NeedHelp i18n={i18n} helpSeekingStudents={helpSeekingStudents} classrooms={classrooms} />) : null}
        <StandardInfo
          i18n={i18n}
          ljName={ljCode}
          externalKey={externalKey}
          description={description}
          count={count}
          displayHideOption={false}
          standardName={standardName} />
        <Preview
          role="button"
          tabIndex="-1"
          onClick={getPreviewInfo}
        ><SvgIcon icon={PreviewDemoIcon} /><span>{i18n.t`Preview question`}</span></Preview>
      </ContextModal>
      <ContextModal
        show={callOut}
        positionArrow={positionArrowCallout}
        positionX={positionXCallout}
        positionY={positionYCallout}
        contextTheme={contextTheme}
        name={ljName} />
    </OutsideClickHandler>
  </Wrapper>);
};

LearningJourney.propTypes = {
  id: PropTypes.string,
  i18n: PropTypes.object,
  ljCode: PropTypes.string,
  ljName: PropTypes.string.isRequired,
  standardName: PropTypes.string,
  externalKey: PropTypes.string,
  description: PropTypes.string,
  count: PropTypes.number
};

LearningJourney.defaultProps = {
  ljName: ''
};

export default LearningJourney;
